import isEmpty from 'lodash/isEmpty'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { useAssetsData } from 'modules/pools/hooks/useAssetsData'
import { useCallback, useMemo, useState } from 'react'
import { useSWRInfinite } from '../hooks/useSwr'

import { BigNumber } from 'ethers'
import { Chains } from 'modules/blockChain/chains'
import { weiToNum } from 'modules/blockChain/utils/parseWei'
import { getERCAssetByAddress } from 'modules/pools/constants/poolAssets'
import { getPoolInvestorByAddress } from 'modules/pools/utils/getPoolInvestorContract'
import { TxType } from 'modules/transactions/types/TxType'
import { GRAPH_URL } from '../networkConfig'
import {
  Args as TransactionRelationsArgs,
  getQueryGraphTransactionRelations,
  TransactionRelationsResponse,
} from '../queries/transactionRelations'
import {
  getQueryGraphTransactions,
  TransactionsResponse,
} from '../queries/transactions'
import { Transaction, TransactionFormatted } from '../types'
import { fetcherGraph } from '../utils/fetcherGraph'

const PAGE_SIZE = 10

export function useGraphTransactionsSWR() {
  const { walletAddress, chainId } = useWeb3()
  const { getAssetDecimals } = useAssetsData()
  const [sortDir, setSortDir] = useState('desc')

  /**
   * Initialize SWR with pagination
   */
  const depositsSwr = useSWRInfinite<{
    txsRaw: Transaction[]
    txsFormatted: TransactionFormatted[]
  }>(
    // SWR paginated key
    (pageIdx, prev) =>
      !prev || prev.txsRaw.length === PAGE_SIZE
        ? [walletAddress, chainId, sortDir, pageIdx]
        : null,
    // SWR request body
    async (
      _walletAddress: string,
      _chainId: Chains,
      _sortDir: 'asc' | 'desc',
      pageIdx: number,
    ) => {
      /**
       * Fetch base transaction list data
       */
      const transactionsQuery = getQueryGraphTransactions({
        first: PAGE_SIZE,
        skip: pageIdx * PAGE_SIZE,
        sortDir: _sortDir,
        walletAddress: _walletAddress,
      })

      const {
        data: { transactions: txs },
      } = await fetcherGraph<TransactionsResponse>(
        GRAPH_URL[_chainId],
        transactionsQuery,
      )

      /**
       * Collect id's of related entities to fetch
       */
      const relationArgs = txs.reduce<
        Omit<TransactionRelationsArgs, 'walletAddress'>
      >(
        (res, curr) => {
          curr.transactionType.forEach(type => {
            switch (type) {
              case TxType.LiquidityDeposited:
                res.depositIds.push(curr.id)
                break
              case TxType.LiquidityWithdrawn:
                res.withdrawalIds.push(curr.id)
                break
              case TxType.BorrowSuccessful:
                res.borrowIds.push(curr.id)
                break
              case TxType.LoanRepaid:
                res.repaymentIds.push(curr.id)
                break
              case TxType.CollateralDeposited:
                res.collateralDepositIds.push(curr.id)
                break
              case TxType.CollateralWithdrawn:
                res.collateralWithdrawalIds.push(curr.id)
                break
              default:
                break
            }
          })
          return res
        },
        {
          depositIds: [],
          withdrawalIds: [],
          borrowIds: [],
          repaymentIds: [],
          collateralDepositIds: [],
          collateralWithdrawalIds: [],
        },
      )

      /**
       * Fetch related entities data
       */
      const { data: relations } = await (async () => {
        const hasAnyRel = Object.values(relationArgs).some(el => !isEmpty(el))
        if (!hasAnyRel) {
          return { data: {} } as TransactionRelationsResponse
        }
        const relationsQuery = getQueryGraphTransactionRelations({
          ...relationArgs,
          walletAddress: _walletAddress.toLowerCase(),
        })
        return fetcherGraph<TransactionRelationsResponse>(
          GRAPH_URL[_chainId],
          relationsQuery,
        )
      })()

      /**
       * Normalizing operation events and fields formatting:
       *    One transaction from subgraph may contain several operation events
       *    packed in array. We are making them flat here to display separatly.
       *    Also related entities data is being collected here.
       */
      const unwrappedTxs = txs.reduce<TransactionFormatted[]>((res, curr) => {
        const unwrapped = curr.transactionType.map<TransactionFormatted>(
          (typeRaw, i, typesArr) => {
            // Transaction type determining
            const type =
              typeRaw in TxType ? TxType[typeRaw as TxType] : TxType.Unknown

            // Related entity data collecting
            const relatedData = (() => {
              const idPlusWallet = `${curr.id}-${_walletAddress}`.toLowerCase()
              const findEntityByIdWallet = (e: { id: string }) =>
                e.id.toLowerCase() === idPlusWallet
              const fomdEnittyByTxHash = (e: { txHash: string }) =>
                e.txHash === curr.id

              switch (type) {
                case TxType.LiquidityDeposited: {
                  const rel = relations.deposits?.find(findEntityByIdWallet)
                  if (!rel) return undefined
                  const { asset } = getPoolInvestorByAddress(chainId, curr.to)
                  return {
                    asset,
                    amount: weiToNum(rel.amount, getAssetDecimals(asset)),
                  }
                }

                case TxType.LiquidityWithdrawn: {
                  const rel = relations.withdrawals?.find(findEntityByIdWallet)
                  if (!rel) return undefined
                  const { asset } = getPoolInvestorByAddress(chainId, curr.to)
                  const dtPriceNormalized = BigNumber.from(rel.dtPrice).div(
                    BigNumber.from(10 ** 12),
                  )
                  const amount =
                    weiToNum(rel.amount, 6) / weiToNum(dtPriceNormalized, 6)

                  return {
                    asset,
                    amount,
                  }
                }

                case TxType.BorrowSuccessful: {
                  const rel = relations.borrows?.find(findEntityByIdWallet)
                  if (!rel) return undefined
                  const token = rel.loanId.erc20Address
                  const asset = getERCAssetByAddress(token, chainId)
                  return {
                    asset,
                    amount: weiToNum(rel.amount, getAssetDecimals(asset)),
                  }
                }

                case TxType.CollateralDeposited: {
                  // Special case for `CollateralDeposited` event in `Borrow` sequence
                  if (typesArr.includes(TxType.BorrowSuccessful)) {
                    const rel = relations.borrows?.find(findEntityByIdWallet)
                    if (!rel) return undefined
                    const token = rel.collateralToken
                    const asset = getERCAssetByAddress(token, chainId)
                    const amount = rel.collateralAmount
                    return {
                      asset: asset,
                      amount: weiToNum(amount, getAssetDecimals(asset)),
                    }
                  }

                  // Normal case
                  const rel =
                    relations.collateralDeposits?.find(fomdEnittyByTxHash)
                  if (!rel) return undefined
                  const token = rel.token
                  const asset = getERCAssetByAddress(token, chainId)
                  return {
                    asset,
                    amount: weiToNum(rel.amount, getAssetDecimals(asset)),
                  }
                }

                case TxType.CollateralWithdrawn: {
                  const rel =
                    relations.collateralWithdrawals?.find(fomdEnittyByTxHash)
                  if (!rel) return undefined
                  const token = rel.token
                  const asset = getERCAssetByAddress(token, chainId)
                  return {
                    asset,
                    amount: weiToNum(rel.amount, getAssetDecimals(asset)),
                  }
                }

                case TxType.LoanRepaid: {
                  const rel = relations.repayments?.find(fomdEnittyByTxHash)
                  if (!rel) return undefined
                  const token = rel.loanId.erc20Address
                  const asset = getERCAssetByAddress(token, chainId)
                  return {
                    asset,
                    amount: weiToNum(rel.amountRepaid, getAssetDecimals(asset)),
                  }
                }

                default:
                  return
              }
            })()

            // Fields formatting
            return {
              ...curr,
              asset: relatedData?.asset,
              amount: relatedData?.amount,
              timestamp: Number(curr.timestamp),
              transactionType: type,
            }
          },
        )
        return [...res, ...unwrapped]
      }, [])

      return {
        txsRaw: txs,
        txsFormatted: unwrappedTxs,
      }
    },
  )

  /**
   * Pagination finishing
   */
  const resultPaged = depositsSwr.data
  const result = useMemo(
    () =>
      resultPaged
        ?.flat()
        .map(a => a.txsFormatted)
        .flat(),
    [resultPaged],
  )
  const { setSize } = depositsSwr
  const loadMore = useCallback(() => setSize(i => i + 1), [setSize])
  const hasMore =
    resultPaged &&
    resultPaged[resultPaged.length - 1].txsRaw.length === PAGE_SIZE

  return {
    ...depositsSwr,
    data: result,
    loadMore,
    hasMore,
    setSortDir,
  }
}
